import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useQuery from "../search/useQuery";
import QUERY_TAGS from "../search/queryTags";
import { EMPTY_PAGE, normalizeSharedPage } from "../finance/financePage";
import { VOLATILE_STALE_TIME } from "../finance/financeCache";
import { fetchMyRatings } from "./reviewsService";
import { mapReviews } from "./reviewsMappers";

export const PAGE_STEP = 20;

const useMyReviews = ({ targetType, pageSize = PAGE_STEP, enabled = true } = {}) => {
  const [size, setSize] = useState(pageSize);
  const settledRef = useRef(EMPTY_PAGE);

  useEffect(() => {
    setSize(pageSize);
    settledRef.current = EMPTY_PAGE;
  }, [targetType, pageSize]);

  const query = useQuery({
    key: enabled ? `reviews:mine:${targetType || "all"}:${size}` : null,
    tag: QUERY_TAGS.MY_RATINGS,
    staleTime: VOLATILE_STALE_TIME,
    enabled,
    fetcher: () => fetchMyRatings({ page: 1, pageSize: size, targetType }),
  });

  const fresh = useMemo(() => normalizeSharedPage(query.currentData), [query.currentData]);
  const hasFresh = query.currentData !== undefined;
  const page = hasFresh ? fresh : settledRef.current;

  useEffect(() => {
    if (hasFresh) settledRef.current = fresh;
  }, [hasFresh, fresh]);

  const items = useMemo(() => mapReviews(page.items), [page]);

  const loadMore = useCallback(() => {
    setSize((current) => current + PAGE_STEP);
  }, []);

  const hasData = hasFresh || settledRef.current !== EMPTY_PAGE;

  return {
    items,
    total: page.total,
    isLoading: !hasData && !query.isError && query.enabled,
    isFetching: query.isFetching,
    isPaging: query.isFetching && hasData,
    isError: query.isError && !hasData,
    error: query.error,
    canLoadMore: !page.isLast && !query.isFetching && !query.isError,
    loadMore,
    refetch: query.refetch,
  };
};

export default useMyReviews;
